import * as React from 'react';
import { z } from 'zod';
import { FormField, type FormSchema, type ZodForm } from './Form';
import { Input, Textarea } from './Input';
import { Grid, Stack } from './Layout';
import { Select, type SelectOption } from './Select';
import { Surface } from './Card';
import { Text } from './Text';

export type LotFormCategory = 'VEHICLE' | 'RECOVERY' | 'EQUIPMENT';
export type LotFormPriceType = 'FIXED' | 'STARTING_BID';

export const LOT_CATEGORY_LABELS: Record<LotFormCategory, string> = {
  VEHICLE: 'Vehicle',
  RECOVERY: 'Recovery',
  EQUIPMENT: 'Equipment',
};

const CATEGORY_OPTIONS: SelectOption[] = Object.entries(
  LOT_CATEGORY_LABELS
).map(([value, label]) => ({ value, label }));

const PRICE_TYPE_OPTIONS: SelectOption[] = [
  { value: 'FIXED', label: 'Fixed price' },
  { value: 'STARTING_BID', label: 'Starting bid' },
];

const KEYS_OPTIONS: SelectOption[] = [
  { value: 'unknown', label: 'Unknown' },
  { value: 'true', label: 'Yes' },
  { value: 'false', label: 'No' },
];

/** A text field that an edit can clear back to nothing. */
const clearableText = z.string().nullish();
/** A number field the input holds as text; '' means cleared. */
const clearableNumber = z
  .union([z.number(), z.literal(''), z.null()])
  .optional();

/**
 * The lot facts, and which of them a lot cannot go without.
 *
 * Year, make and model are required **only for the Vehicle category** —
 * a recovery or a piece of equipment has none of them. That is a refinement
 * rather than three `.min(1)`s, and the markers on those three fields follow
 * the same condition rather than being set by hand.
 */
export const lotFormSchema = z
  .object({
    title: z
      .string()
      .trim()
      .min(1, 'Give the vehicle a title — it headlines the public listing.'),
    category: z.enum(['VEHICLE', 'RECOVERY', 'EQUIPMENT']).default('VEHICLE'),
    priceType: z.enum(['FIXED', 'STARTING_BID']).default('FIXED'),
    price: z
      .number({
        required_error: 'Set a price — the listing goes live with it.',
        invalid_type_error: 'Set a price — the listing goes live with it.',
      })
      .min(0, 'Price can’t be negative.'),
    conditionNotes: clearableText,
    vehicleYear: clearableNumber,
    vehicleMake: clearableText,
    vehicleModel: clearableText,
    vin: clearableText,
    licensePlate: clearableText,
    odometer: clearableNumber,
    bodyType: clearableText,
    transmission: clearableText,
    hasKeys: z.boolean().nullish(),
    titleStatus: clearableText,
  })
  .superRefine((values, ctx) => {
    if (values.category !== 'VEHICLE') return;
    if (typeof values.vehicleYear !== 'number') {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['vehicleYear'],
        message: 'Year is required for vehicles.',
      });
    }
    if (!values.vehicleMake?.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['vehicleMake'],
        message: 'Make is required for vehicles.',
      });
    }
    if (!values.vehicleModel?.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['vehicleModel'],
        message: 'Model is required for vehicles.',
      });
    }
  });

/**
 * The facts the form edits. Optional fields accept null so an edit can clear
 * a stored value; the container decides what a cleared value is sent as.
 */
export type LotFormValues = z.infer<typeof lotFormSchema>;

export interface LotFormFieldsProps<S extends FormSchema> {
  /** The dialog's form; its schema is `lotFormSchema`. */
  form: ZodForm<S>;
}

/**
 * The lot facts form: title, category, price, vehicle details and condition
 * notes. Shared by the Add-lot dialog and the detail drawer's inline edit.
 * Photos and documents are not here — the lot's media section owns them.
 *
 *   <LotFormFields form={form} />
 *
 * The vehicle block only shows for the Vehicle category.
 */
export function LotFormFields<S extends FormSchema>({
  form,
}: LotFormFieldsProps<S>) {
  // See SaleRecordFields: the caller's schema is this one, which TypeScript
  // cannot see through the generic.
  const f = form as unknown as ZodForm<typeof lotFormSchema>;
  const isVehicle = f.watch('category') === 'VEHICLE';

  /** A number input, with '' standing in for a cleared value. */
  const numberInput = (
    field: {
      name: string;
      value: number | '' | null | undefined;
      onChange: (value: number | '') => void;
      onBlur: () => void;
      ref: React.Ref<HTMLInputElement>;
    },
    props: { min?: number; placeholder?: string } = {}
  ) => (
    <Input
      type="number"
      name={field.name}
      ref={field.ref}
      onBlur={field.onBlur}
      value={field.value ?? ''}
      onChange={(e) =>
        field.onChange(e.target.value === '' ? '' : Number(e.target.value))
      }
      {...props}
    />
  );

  return (
    <Stack gap={4}>
      <Grid cols={{ base: 1, sm: 3 }} gap={4}>
        <FormField
          form={f}
          name="title"
          label="Title"
          className="sm:col-span-2"
        >
          {(field) => <Input {...field} placeholder="2005 Honda Accord" />}
        </FormField>
        <FormField form={f} name="category" label="Category">
          {(field) => (
            <Select
              options={CATEGORY_OPTIONS}
              value={field.value}
              onValueChange={field.onChange}
            />
          )}
        </FormField>
      </Grid>

      <Grid cols={{ base: 1, sm: 3 }} gap={4}>
        <FormField
          form={f}
          name="price"
          label="Price (USD)"
          className="sm:col-span-2"
        >
          {(field) => numberInput(field, { min: 0, placeholder: '12000' })}
        </FormField>
        <FormField form={f} name="priceType" label="Price type">
          {(field) => (
            <Select
              options={PRICE_TYPE_OPTIONS}
              value={field.value}
              onValueChange={field.onChange}
            />
          )}
        </FormField>
      </Grid>

      {isVehicle && (
        <Surface radius="lg" padding="md">
          <Stack gap={4}>
            <Text variant="body-sm-strong">Vehicle details</Text>
            <Grid cols={{ base: 2, sm: 3 }} gap={4}>
              {/* Required only for a vehicle, which the refinement enforces
                  and these three markers follow. */}
              <FormField form={f} name="vehicleYear" label="Year" required>
                {(field) => numberInput(field)}
              </FormField>
              <FormField form={f} name="vehicleMake" label="Make" required>
                {(field) => <Input {...field} value={field.value ?? ''} />}
              </FormField>
              <FormField form={f} name="vehicleModel" label="Model" required>
                {(field) => <Input {...field} value={field.value ?? ''} />}
              </FormField>
              <FormField form={f} name="vin" label="VIN">
                {(field) => <Input {...field} value={field.value ?? ''} />}
              </FormField>
              <FormField form={f} name="licensePlate" label="License plate">
                {(field) => <Input {...field} value={field.value ?? ''} />}
              </FormField>
              <FormField form={f} name="odometer" label="Odometer">
                {(field) => numberInput(field)}
              </FormField>
              <FormField form={f} name="bodyType" label="Body type">
                {(field) => (
                  <Input
                    {...field}
                    value={field.value ?? ''}
                    placeholder="4-door sedan"
                  />
                )}
              </FormField>
              <FormField form={f} name="transmission" label="Transmission">
                {(field) => (
                  <Input
                    {...field}
                    value={field.value ?? ''}
                    placeholder="Automatic"
                  />
                )}
              </FormField>
              <FormField form={f} name="hasKeys" label="Keys">
                {(field) => (
                  <Select
                    options={KEYS_OPTIONS}
                    value={
                      field.value === null || field.value === undefined
                        ? 'unknown'
                        : String(field.value)
                    }
                    onValueChange={(v) =>
                      field.onChange(v === 'unknown' ? null : v === 'true')
                    }
                  />
                )}
              </FormField>
              <FormField form={f} name="titleStatus" label="Title status">
                {(field) => (
                  <Input
                    {...field}
                    value={field.value ?? ''}
                    placeholder="Lien sale"
                  />
                )}
              </FormField>
            </Grid>
          </Stack>
        </Surface>
      )}

      <FormField form={f} name="conditionNotes" label="Condition notes">
        {(field) => (
          <Textarea
            {...field}
            value={field.value ?? ''}
            rows={3}
            placeholder="Front-end collision damage; engine turns over. Interior in fair condition."
          />
        )}
      </FormField>
    </Stack>
  );
}
